import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, Table, Typography, Tag } from 'antd';
import {
  DollarOutlined,
  FileProtectOutlined,
  PercentageOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, LineChart, Line, Area, AreaChart, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

const { Text } = Typography;

const ENTITY_COLORS = {
  'Shine Hub': '#54A2CC',
  'PW Renewable': '#05A677',
  'GTL Renewable': '#f5b759',
};

const fmt = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(0)}`;
};

const typeColor = (type) => {
  switch (type) {
    case 'Director Loan': return 'blue';
    case 'Loan': return 'orange';
    case 'AP': return 'red';
    default: return 'default';
  }
};

const loanColumns = [
  {
    title: 'Entity', dataIndex: 'entity', key: 'entity', width: 130, fixed: 'left',
    render: (v) => <Text strong style={{ color: ENTITY_COLORS[v] || '#333' }}>{v}</Text>,
  },
  { title: 'Account', dataIndex: 'accountName', key: 'accountName', width: 220, ellipsis: true },
  {
    title: 'Type', dataIndex: 'type', key: 'type', width: 120,
    render: (v) => <Tag color={typeColor(v)}>{v}</Tag>,
  },
  {
    title: 'Rate', dataIndex: 'interestRate', key: 'interestRate', width: 80, align: 'right',
    render: (v) => v != null ? `${v}%` : '-',
  },
  { title: 'Frequency', dataIndex: 'repaymentFrequency', key: 'repaymentFrequency', width: 110, responsive: ['lg'] },
  {
    /**
     * Rows carrying an asOf came from Xero rather than the sheet's latest
     * month column, and so are dated days rather than a month apart from the
     * rows around them. The date is shown because a table that silently mixes
     * two as-at dates reads as though every figure shares the header's.
     */
    title: 'Current Balance', dataIndex: 'currentBalance', key: 'currentBalance', width: 150, align: 'right',
    render: (v, r) => (
      <>
        <Text strong>{fmt(v)}</Text>
        {r.asOf && (
          <div><Text type="secondary" style={{ fontSize: 11 }}>{r.source} · {r.asOf}</Text></div>
        )}
      </>
    ),
    sorter: (a, b) => a.currentBalance - b.currentBalance,
  },
  {
    title: 'Previous', dataIndex: 'prevBalance', key: 'prevBalance', width: 130, align: 'right',
    render: (v, r) => (
      <>
        {fmt(v)}
        {r.prevAsOf && (
          <div><Text type="secondary" style={{ fontSize: 11 }}>{r.prevAsOf}</Text></div>
        )}
      </>
    ),
    responsive: ['md'],
  },
  {
    title: 'Change', dataIndex: 'change', key: 'change', width: 120, align: 'right',
    render: (v) => {
      if (!v) return <Text type="secondary">-</Text>;
      const color = v > 0 ? '#FA5252' : '#05A677';
      const prefix = v > 0 ? '+' : '';
      return <Text style={{ color, fontWeight: 600 }}>{prefix}{fmt(v)}</Text>;
    },
    responsive: ['md'],
  },
];

export default function LiabilityList() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/liability-list/summary');
        setData(res.data.data);
      } catch { /* handled by interceptor */ }
      setLoading(false);
    })();
  }, []);

  const kpis = data?.kpis || {};
  const loans = data?.loans || [];
  const entityTotals = data?.entityTotals || [];
  const monthlyTrend = data?.monthlyTrend || [];
  const entities = data?.entities || [];
  const gtlDetail = data?.gtlLoanDetail || [];
  const pwDetail = data?.pwLoanDetail || {};

  // Only active loans (filter out paid-off)
  const activeLoans = loans.filter(l => l.currentBalance > 0);

  // Bar chart: current balance by entity
  const entityBarData = entityTotals
    .filter(e => e.total > 0)
    .map(e => ({
      name: e.entity,
      total: e.total,
      fill: ENTITY_COLORS[e.entity] || '#8884d8',
    }));

  // GTL InfraDebt breakdown for area chart
  const gtlAreaData = gtlDetail.map(g => ({
    name: g.month.replace(/^\d+\s/, '').substring(0, 3) + ' ' + g.month.slice(-4),
    'Term Balance': g.termBalance,
    'Capex Balance': g.capexBalance,
  }));

  /**
   * PW Trade Finance outstanding, one point per Friday for the last 12 months.
   *
   * This used to sum a hand-transcribed transaction list into a running
   * balance, which left the line ending on the July figure while Xero had
   * moved on, and could not have been right anyway — the account also moves on
   * journals, which never appear in that list. These come from Xero's balance
   * sheet via the database instead.
   */
  const weekly = data?.loanWeekly || [];
  const pwBalanceData = weekly
    .filter(p => p.accountCode === '22100')
    .map(p => ({ name: p.date, balance: p.balance }));
  const pwAsOf = pwBalanceData.length ? pwBalanceData[pwBalanceData.length - 1].name : null;

  // Table data with totals row
  const tableData = [
    ...activeLoans.map((l, i) => ({ ...l, key: i })),
    {
      key: 'total',
      entity: 'Total',
      accountName: `${kpis.activeLoans} active loans`,
      type: '',
      currentBalance: kpis.totalOutstanding,
      prevBalance: activeLoans.reduce((s, l) => s + l.prevBalance, 0),
      change: activeLoans.reduce((s, l) => s + l.change, 0),
    },
  ];

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Liability List</h1>
        <p>Debt and liability overview across all entities (Google Sheets; PW trade finance from Xero)</p>
      </div>
      <Spin spinning={loading}>
        {/* KPI Scorecards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title="Total Outstanding"
                value={kpis.totalOutstanding}
                formatter={(v) => fmt(v)}
                prefix={<DollarOutlined style={{ color: '#FA5252' }} />}
              />
              {kpis.latestMonth && <Text type="secondary" style={{ fontSize: 12 }}>As of {kpis.latestMonth}</Text>}
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title="Active Loans"
                value={kpis.activeLoans}
                suffix={<Text type="secondary" style={{ fontSize: 14 }}>/ {kpis.totalLoans}</Text>}
                prefix={<FileProtectOutlined style={{ color: '#54A2CC' }} />}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {kpis.totalLoans - kpis.activeLoans} fully paid
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title="Weighted Avg Rate"
                value={kpis.weightedAvgRate}
                suffix="%"
                precision={2}
                prefix={<PercentageOutlined style={{ color: '#f5b759' }} />}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>On interest-bearing debt</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title="Entities"
                value={entities.length}
                prefix={<CheckCircleOutlined style={{ color: '#05A677' }} />}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {entities.join(', ')}
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Charts Row */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {/* Debt Trend Line Chart */}
          <Col xs={24} lg={14}>
            {monthlyTrend.length > 0 && (
              <Card title="Monthly Debt Trend by Entity">
                <ResponsiveContainer width="100%" height={340}>
                  <LineChart data={monthlyTrend} margin={{ top: 5, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" fontSize={11} />
                    <YAxis tickFormatter={fmt} fontSize={11} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend />
                    {entities.map(ent => (
                      <Line
                        key={ent}
                        type="monotone"
                        dataKey={ent}
                        stroke={ENTITY_COLORS[ent] || '#8884d8'}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="#333"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Group Total"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            )}
          </Col>

          {/* Entity Breakdown Bar Chart */}
          <Col xs={24} lg={10}>
            {entityBarData.length > 0 && (
              <Card title="Current Debt by Entity">
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={entityBarData} margin={{ top: 5, right: 20, bottom: 60, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-20} textAnchor="end" fontSize={11} />
                    <YAxis tickFormatter={fmt} fontSize={11} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Bar dataKey="total" name="Outstanding" radius={[4, 4, 0, 0]}>
                      {entityBarData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}
          </Col>
        </Row>

        {/* Detail Charts Row */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {/* PW Trade Finance Balance Trend */}
          <Col xs={24} lg={12}>
            {pwBalanceData.length > 0 && (
              <Card title={`PW Trade Finance Outstanding${pwAsOf ? ` (weekly, to ${pwAsOf})` : ''}`}>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={pwBalanceData} margin={{ top: 5, right: 20, bottom: 60, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" fontSize={10} interval="preserveStartEnd" />
                    <YAxis tickFormatter={fmt} fontSize={11} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Area type="stepAfter" dataKey="balance" stroke="#05A677" fill="#05A677" fillOpacity={0.15} strokeWidth={2} name="Outstanding Balance" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            )}
          </Col>

          {/* GTL InfraDebt Breakdown */}
          <Col xs={24} lg={12}>
            {gtlAreaData.length > 0 && (
              <Card title="GTL InfraDebt Loan Breakdown">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={gtlAreaData} margin={{ top: 5, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis tickFormatter={fmt} fontSize={11} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend />
                    <Area type="monotone" dataKey="Term Balance" stackId="1" fill="#54A2CC" stroke="#54A2CC" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="Capex Balance" stackId="1" fill="#f5b759" stroke="#f5b759" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            )}
          </Col>
        </Row>

        {/* Detailed Loan Table */}
        {activeLoans.length > 0 && (
          <Card style={{ marginTop: 16 }} title="Loan Details">
            <Table
              dataSource={tableData}
              columns={loanColumns}
              pagination={false}
              size="small"
              scroll={{ x: 1000 }}
              rowClassName={(r) => r.key === 'total' ? 'table-row-bold' : ''}
            />
          </Card>
        )}
      </Spin>
    </PageLayout>
  );
}
